import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartConfiguration, ChartType, Chart, registerables } from 'chart.js';

// Register all Chart.js controllers, scales, and plugins globally.
// This prevents "not a registered controller" errors when instantiated via raw DOM injection.
Chart.register(...registerables);

@Component({
  selector: 'rapider-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      style="position: relative; width: 100%; height: 100%; min-height: 250px;"
    >
      <canvas #chartCanvas></canvas>
    </div>
  `,
  host: {
    style: 'display: block; width: 100%; height: 100%;',
  },
})
export class RapiderChartComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  // CRITICAL FIX: Explicitly bind the kebab-case HTML attributes so Angular Elements
  // maps them flawlessly, avoiding auto-camelCase resolution failures.
  @Input('chart-data') chartData: string | any = '{}';
  @Input('chart-options') chartOptions: string | any = '{}';
  @Input('chart-type') chartType: string = 'bar';

  @ViewChild('chartCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private chartInstance: Chart | null = null;

  // CRITICAL FIX: Manually trigger change detection as the iframe sandbox might suppress Zone.js ticks
  private cdr = inject(ChangeDetectorRef);

  public parsedData: ChartConfiguration['data'] = { datasets: [] };
  public parsedOptions: ChartConfiguration['options'] = {};
  public parsedType: ChartType = 'bar';

  ngOnInit() {
    // Ensure initial attributes passed directly into the HTML tag are parsed
    this.parseInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.parseInputs();
    if (this.canvasRef?.nativeElement) {
      this.renderChart();
    }
  }

  ngAfterViewInit() {
    // Ensure rendering happens after the canvas element is securely attached to the DOM.
    setTimeout(() => {
      this.renderChart();
    }, 50);
  }

  ngOnDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  /**
   * PURE FUNCTION: Safely extracts and parses the stringified JSON attributes.
   * Handles edge cases where Angular might pass the object directly instead of a string.
   */
  private parseInputs() {
    try {
      this.parsedData =
        typeof this.chartData === 'string'
          ? JSON.parse(this.chartData || '{}')
          : this.chartData;
    } catch (e) {
      console.warn('[RapiderChart] Failed to parse chart-data', e);
    }

    try {
      this.parsedOptions =
        typeof this.chartOptions === 'string'
          ? JSON.parse(this.chartOptions || '{}')
          : this.chartOptions;
    } catch (e) {
      console.warn('[RapiderChart] Failed to parse chart-options', e);
    }

    this.parsedType = (this.chartType as ChartType) || 'bar';

    // Force the view to update natively to paint the canvas wrapper
    this.cdr.detectChanges();
  }

  private renderChart() {
    if (!this.canvasRef?.nativeElement) return;

    try {
      if (this.chartInstance) {
        // CRITICAL FIX: Chart.js does not expose a mutable `type` property on the root instance in TS.
        // If the chart type changes dynamically, we MUST destroy and recreate it.
        // If only data/options change, we update it in place to avoid blank canvas flashing.
        if ((this.chartInstance.config as any).type !== this.parsedType) {
          this.chartInstance.destroy();
          this.chartInstance = new Chart(this.canvasRef.nativeElement, {
            type: this.parsedType,
            data: this.parsedData,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              ...this.parsedOptions,
            },
          });
        } else {
          this.chartInstance.data = this.parsedData;
          this.chartInstance.options = {
            responsive: true,
            maintainAspectRatio: false,
            ...this.parsedOptions,
          };
          this.chartInstance.update();
        }
      } else {
        this.chartInstance = new Chart(this.canvasRef.nativeElement, {
          type: this.parsedType,
          data: this.parsedData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            ...this.parsedOptions,
          },
        });
      }
    } catch (error) {
      console.error('[RapiderChart] Failed to render Chart.js instance', error);
    }
  }
}
